import { API_URLS } from '@chessx/shared/api-routes'
import { create } from 'zustand'
import axiosInstance from '../network/axiosInstance'
import { toastMixin } from '../utils/interact'
import type { ApiError } from '../types/api'

const BASE_URL = API_URLS.APP_CONFIG

export interface AppConfig {
  key: string
  value: unknown
  description?: string
  [k: string]: unknown
}

interface AppConfigState {
  configs: AppConfig[]
  loading: boolean
  fetchConfigs: () => Promise<void>
  createConfig: (key: string, value: unknown, description?: string) => Promise<void>
  updateConfig: (key: string, value: unknown, description?: string) => Promise<void>
  deleteConfig: (key: string) => Promise<void>
}

const useAppConfigStore = create<AppConfigState>()((set, get) => ({
  configs: [],
  loading: false,

  fetchConfigs: async () => {
    try {
      set({ loading: true })
      const res = await axiosInstance.get(BASE_URL)
      if (res?.status === 200) {
        set({ configs: res.data.data })
      }
    } catch (e) {
      const err = e as ApiError
      toastMixin.fire({
        title: `Error fetching configs: ${err?.response?.data?.error || err.message}`,
        icon: 'error'
      })
    } finally {
      set({ loading: false })
    }
  },

  createConfig: async (key, value, description) => {
    try {
      const res = await axiosInstance.post(BASE_URL, { key, value, description })
      if (res?.status === 200) {
        toastMixin.fire({ title: 'Config created successfully!' })
        get().fetchConfigs()
      }
    } catch (e) {
      const err = e as ApiError
      toastMixin.fire({
        title: `Error creating config: ${err?.response?.data?.error || err.message}`,
        icon: 'error'
      })
    }
  },

  updateConfig: async (key, value, description) => {
    try {
      const res = await axiosInstance.put(BASE_URL, { key, value, description })
      if (res?.status === 200) {
        toastMixin.fire({ title: 'Config updated successfully!' })
        get().fetchConfigs()
      }
    } catch (e) {
      const err = e as ApiError
      toastMixin.fire({
        title: `Error updating config: ${err?.response?.data?.error || err.message}`,
        icon: 'error'
      })
    }
  },

  deleteConfig: async key => {
    try {
      const res = await axiosInstance.delete(BASE_URL, { data: { key } })
      if (res?.status === 200) {
        toastMixin.fire({ title: 'Config deleted successfully!' })
        get().fetchConfigs()
      }
    } catch (e) {
      const err = e as ApiError
      toastMixin.fire({
        title: `Error deleting config: ${err?.response?.data?.error || err.message}`,
        icon: 'error'
      })
    }
  }
}))

export default useAppConfigStore
